






import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  ArrowLeft, User, MapPin, CreditCard, Briefcase, 
  FileText, Calendar, Mail, Phone, Hash, Trash2, 
  Download, Loader2, Clock, ExternalLink,
  DollarSign, Map, ShieldCheck, UserCheck, Printer, Check, Info, Layers
} from "lucide-react";

const MemberDetailsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [member, setMember] = useState(null);
const [receipts, setReceipts] = useState([]); 
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [printSections, setPrintSections] = useState({
    personal: true,
    finance: true,
    receipts: false, 
    executives: true,
    documents: false 
  });

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const mRes = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/${id}`);
      const mData = await mRes.json();
      setMember(mData);

      const rRes = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_BASE}/GetByMember/${id}`);
const rData = await rRes.json();
setReceipts(rData || []);

      const dRes = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_DOC_GET_BY_MEMBER}/${id}`);
      const dData = await dRes.json();
      setDocs(dData || []);
    } catch (err) {
      Swal.fire("Error", "Could not load member profile", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const handleDeleteDoc = async (docId) => {
    const result = await Swal.fire({ 
        title: 'Delete Document?', 
        text: "This cannot be undone.",
        icon: 'warning', 
        showCancelButton: true,
        confirmButtonColor: '#ef4444'
    });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_DOC_DELETE}/${docId}`, { method: "DELETE" });
      fetchAllData();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    topNav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    backBtn: { background: '#fff', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#475569' },
    printPanel: { background: '#fff', padding: '20px', borderRadius: '20px', marginBottom: '25px', border: '1.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    checkboxGroup: { display: 'flex', gap: '20px' },
    checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '700', color: '#1b2559', cursor: 'pointer' },
    printBtn: { background: '#1b2559', color: '#fff', border: 'none', padding: '12px 25px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' },
    mainCard: { background: '#fff', borderRadius: '30px', padding: '40px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', position: 'relative' },
    section: { marginBottom: '35px', borderBottom: '1.5px solid #f1f5f9', paddingBottom: '25px' },
    sectionLabel: { fontSize: '13px', fontWeight: '900', color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '25px' },
    item: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '10px', color: '#a3aed0', fontWeight: '800', textTransform: 'uppercase' },
    value: { fontSize: '14px', color: '#1b2559', fontWeight: '700' },
    paymentTable: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px', fontSize: '11px', color: '#a3aed0', borderBottom: '2px solid #f1f5f9', textTransform: 'uppercase' },
    td: { padding: '12px', fontSize: '13px', color: '#1b2559', fontWeight: '600', borderBottom: '1px solid #f8fafc' },
    docBox: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '15px', borderRadius: '15px', border: '1px solid #e2e8f0' },
    badge: (type) => ({
        padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800',
        backgroundColor: type?.toLowerCase().includes('installment') ? '#eff6ff' : '#ecfdf5',
        color: type?.toLowerCase().includes('installment') ? '#3b82f6' : '#10b981'
    })
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0001") || dateStr.startsWith("0000")) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) return (
    <div style={{height:'100vh', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', background:'#f4f7fe'}}>
        <Loader2 size={50} className="animate-spin" color="#4f46e5"/>
        <p style={{marginTop:'15px', color:'#1b2559', fontWeight:'700'}}>Syncing Profile Data...</p>
    </div>
  );

  return (
    <>
      <div className="no-print"><Navbar /></div>
      <style>
        {`
          @media print {
            @page { size: A4; margin: 10mm; }
            body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
            .no-print, nav, header { display: none !important; }
            .print-container { padding: 0 !important; box-shadow: none !important; border: none !important; width: 100% !important; }
            .section-break { page-break-inside: avoid; margin-bottom: 20px !important; }
          }
        `}
      </style>

      <div style={styles.container} className="print-container">
        <div style={styles.topNav} className="no-print">
          <button style={styles.backBtn} onClick={() => navigate("/members")}>
            <ArrowLeft size={18}/> Back
          </button>
          <button style={{...styles.backBtn, borderColor:'#4f46e5', color:'#4f46e5'}} onClick={() => navigate(`/members/manage?id=${id}`)}>
            Edit Member
          </button>
        </div>

        <div style={styles.printPanel} className="no-print">
           <div style={styles.checkboxGroup}>
              {Object.keys(printSections).map(key => (
                <label key={key} style={styles.checkboxLabel}>
                   <input 
                    type="checkbox" 
                    checked={printSections[key]} 
                    onChange={() => setPrintSections(prev => ({...prev, [key]: !prev[key]}))}
                   />
                   {key.charAt(0).toUpperCase() + key.slice(1)} Info
                </label>
              ))}
           </div>
           <button style={styles.printBtn} onClick={handlePrint}>
              <Printer size={18}/> Print Profile (A4)
           </button>
        </div>

        <div style={styles.mainCard}>
          {/* HEADER SECTION (Removed Logo Box) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
             <div style={{ display:'flex', gap:'25px', alignItems:'center' }}>
                <div>
                   <h1 style={{ margin:0, color:'#1b2559', fontSize:'32px', fontWeight:'900', letterSpacing:'-1px' }}>{member.memberName}</h1>
                   {/* PLOT LIST IN HEADER */}
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                        {member.memberPlots && member.memberPlots.length > 0 ? (
                          member.memberPlots.map((mp, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', color: '#4f46e5', fontSize: '13px' }}>
                              <Map size={16} color="#6366f1"/> 
                              {mp.plot.plotNumber} / {mp.plot.totalArea} Sq. Yds / {mp.plot.project?.projectName || 'N/A'}
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '13px', color: '#a3aed0', fontWeight: '700' }}>No Plot Assigned</div>
                        )}
                   </div>
                </div>
             </div>
             <div style={{ textAlign:'right' }}>
                <div style={{ background:'#f0fdf4', color:'#16a34a', padding:'10px 25px', borderRadius:'15px', fontWeight:'900', fontSize:'13px', border:'1.5px solid #bcf0da', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                   <ShieldCheck size={16}/> Active Member
                </div>
                <div style={{ marginTop:'10px', fontSize:'12px', color:'#a3aed0', fontWeight:'700' }}>
                    Registered: {formatDate(member.createdDate)}
                </div>
             </div>
          </div>

          {/* PERSONAL INFORMATION */}
          {printSections.personal && (
            <div style={styles.section} className="section-break">
               <div style={styles.sectionLabel}><UserCheck size={18}/> Personal Information</div>
               <div style={styles.grid}>
                  <div style={styles.item}><span style={styles.label}>Full Name</span><span style={styles.value}>{member.memberName}</span></div>
                 
                  <div style={styles.item}><span style={styles.label}>Aadhar Number</span><span style={styles.value}>{member.aadharNo || "N/A"}</span></div>
                 
                  <div style={styles.item}><span style={styles.label}>Date of Birth</span><span style={styles.value}>{formatDate(member.dateofBirth)}</span></div>
                  <div style={styles.item}><span style={styles.label}>Spouse Name</span><span style={styles.value}>{member.name2 || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Spouse DOB</span><span style={styles.value}>{formatDate(member.doB2)}</span></div>
                  <div style={styles.item}><span style={styles.label}>Wedding Anniversary</span><span style={styles.value}>{formatDate(member.marriageDate)}</span></div>
                  <div style={styles.item}><span style={styles.label}>Nominee Details</span><span style={styles.value}>{member.nomineeName} ({member.relationship})</span></div>
                  <div style={styles.item}><span style={styles.label}>Mobile</span><span style={styles.value}>{member.phoneNo}</span></div>
                  <div style={styles.item}><span style={styles.label}>Email</span><span style={styles.value}>{member.email || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Home / Work Tel</span><span style={styles.value}>{member.homeTelephone || "N/A"} / {member.workTelephone || "N/A"}</span></div>
                  <div style={{...styles.item, gridColumn:'span 3'}}><span style={styles.label}>Residential Address</span><span style={styles.value}>{member.address}, {member.city}, {member.postCode}</span></div>
               </div>
            </div>
          )}

          {/* FINANCIAL SUMMARY */}
          {printSections.finance && (
            <div style={styles.section} className="section-break">
               <div style={styles.sectionLabel}><DollarSign size={18}/> Financial Summary</div>
               <div style={styles.grid}>
                  {/* DETAILED PLOT ASSIGNMENT LIST */}
                  <div style={{...styles.item, gridColumn: 'span 2'}}>
                    <span style={styles.label}>Assigned Plots (Full Details)</span>
                    <div style={{display:'flex', flexDirection:'column', gap:'5px', marginTop:'5px'}}>
                        {member.memberPlots?.map((mp, idx) => (
                            <span key={idx} style={{...styles.value, fontSize: '13px'}}>• {mp.plot.plotNumber} ({mp.plot.totalArea} Yds) - {mp.plot.project?.projectName}</span>
                        ))}
                    </div>
                  </div>
                  
                  <div style={styles.item}><span style={styles.label}>Membership No</span><span style={styles.value}>{member.membershipNo}</span></div>
                  <div style={styles.item}><span style={styles.label}>Member Type</span><span style={styles.value}>{member.memberType}</span></div>

                  <div style={styles.item}><span style={styles.label}>Total Plot Value</span><span style={{...styles.value, color:'#4f46e5', fontSize: '18px'}}>₹ {member.balanceAmount?.toLocaleString()}</span></div>
                  <div style={styles.item}><span style={styles.label}>Amount Paid (Sum)</span><span style={{...styles.value, color:'#10b981', fontSize: '18px'}}>₹ {member.paidAmount?.toLocaleString()}</span></div>
                  <div style={styles.item}><span style={styles.label}>Current Balance</span><span style={{...styles.value, color:'#ef4444', fontSize: '18px'}}>₹ {(member.balanceAmount - member.paidAmount).toLocaleString()}</span></div>
                  <div style={styles.item}><span style={styles.label}>EMI Details</span><span style={styles.value}>{member.emi} - ₹ {member.emiPerAmount} ({member.noOfYears} Yrs)</span></div>
                  
                  <div style={styles.item}><span style={styles.label}>Salary</span><span style={styles.value}>₹ {parseFloat(member.salary).toLocaleString()}</span></div>
                  <div style={styles.item}><span style={styles.label}>AMC Fee</span><span style={styles.value}>₹ {parseFloat(member.amc).toLocaleString()}</span></div>
               </div>
            </div>
          )}

          {/* PAYMENT HISTORY */}
          {/* RECEIPT HISTORY */}
{printSections.receipts && (
  <div style={styles.section} className="section-break">
     <div style={styles.sectionLabel}><CreditCard size={18}/> Official Receipt History</div>
     <table style={styles.paymentTable}>
         <thead>
         <tr>
             <th style={styles.th}>Receipt No</th>
             <th style={styles.th}>Date</th>
             <th style={styles.th}>Amount</th>
             <th style={styles.th}>Method</th>
             <th style={styles.th}>Status</th>
         </tr>
         </thead>
         <tbody>
         {receipts.length > 0 ? receipts.map((r, index) => (
             <tr key={index}>
                 <td style={styles.td}>{r.receiptNo}</td>
                 <td style={styles.td}>{formatDate(r.paymentDate)}</td>
                 <td style={{...styles.td, color:'#059669', fontWeight:'800'}}>₹ {r.amount?.toLocaleString()}</td>
                 <td style={styles.td}>{r.paymentMode}</td>
                 <td style={styles.td}>
                    <span style={styles.badge(r.status)}>{r.status}</span>
                 </td>
             </tr>
         )) : (
             <tr><td colSpan="5" style={{textAlign:'center', padding:'30px', color:'#a3aed0'}}>No receipts found.</td></tr>
         )}
         </tbody>
     </table>
  </div>
)}

          {/* FIELD EXECUTIVES */}
          {printSections.executives && (
            <div style={styles.section} className="section-break">
               <div style={styles.sectionLabel}><Layers size={18}/> Field & Management Executives</div>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px' }}>
                  <div style={styles.item}><span style={styles.label}>Sales Executive</span><span style={styles.value}>{member.salesExecutive || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Branch Manager</span><span style={styles.value}>{member.branchManager || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Tele Executive</span><span style={styles.value}>{member.teleExecutive || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>BD Executive</span><span style={styles.value}>{member.bdExecutive || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Venue Manager</span><span style={styles.value}>{member.venueManager || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Branch Incharge</span><span style={styles.value}>{member.branchIncharge || "N/A"}</span></div>
                  <div style={styles.item}><span style={styles.label}>Take Over Mgr</span><span style={styles.value}>{member.takeOverManager || "N/A"}</span></div>
                  
               </div>
            </div>
          )}

          {/* DOCUMENT VAULT */}
          {printSections.documents && (
            <div style={{...styles.section, borderBottom:'none', paddingBottom:0}} className="section-break">
               <div style={styles.sectionLabel}><FileText size={18}/> Document Vault Summary</div>
               <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'20px' }}>
                  {docs.length === 0 ? <p style={{fontSize:'13px', color:'#a3aed0', fontWeight:'600'}}>No documents archived for this member.</p> : 
                  docs.map(d => (
                      <div key={d.documentId} style={styles.docBox}>
                         <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
                            <div style={{background:'#eef2ff', padding:'8px', borderRadius:'10px'}}><FileText size={20} color="#4f46e5" /></div>
                            <div>
                               <div style={{fontWeight:'800', color:'#1b2559', fontSize:'13px'}}>{d.documentName}</div>
                               <div style={{fontSize:'11px', color:'#a3aed0', fontWeight:'600'}}>{formatDate(d.uploadDate || member.createdDate)}</div>
                            </div>
                         </div>
                         <div style={{display:'flex', gap:'8px'}} className="no-print">
                            <a href={`${APIConfig.APIACTIVEURL.replace('/api', '')}/${d.filePath}`} target="_blank" rel="noreferrer" style={{color:'#4f46e5', padding:'5px'}}><Download size={18}/></a>
                            <button onClick={() => handleDeleteDoc(d.documentId)} style={{border:'none', background:'none', color:'#ef4444', cursor:'pointer', padding:'5px'}}><Trash2 size={18}/></button>
                         </div>
                      </div>
                  ))}
               </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MemberDetailsView;